import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import api from '../utils/api';
import { toast } from 'sonner';
import { FileText, CheckCircle, AlertCircle, Loader2, Calendar, User, Mail, PenTool, Type, RotateCcw } from 'lucide-react';

const SignContract = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [contract, setContract] = useState(null);
  const [recipientInfo, setRecipientInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(false);
  const [signed, setSigned] = useState(false);
  const [error, setError] = useState(null);
  const [signature, setSignature] = useState('');
  const [signatureType, setSignatureType] = useState('typed'); // 'typed' or 'drawn'
  const [isDrawing, setIsDrawing] = useState(false);
  const canvasRef = useRef(null);
  const [signatureImage, setSignatureImage] = useState(null);

  useEffect(() => {
    fetchContractForSigning();
  }, [token]);

  const fetchContractForSigning = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/public/contracts/sign/${token}`);
      const contractData = res.data.contract;
      const signatures = res.data.signatures || [];
      
      setContract(contractData);
      
      // Check if already signed
      if (signatures.length > 0) {
        setSigned(true);
        toast.info('This contract has already been signed');
      }
      
      // Pre-fill signature if client info available
      if (contractData.client_id) {
        // Get client info from contract
        setSignature(''); // Will be filled by user
      }
    } catch (error) {
      console.error('Failed to load contract:', error);
      setError(error.response?.data?.detail || 'Failed to load contract. The link may be invalid or expired.');
    } finally {
      setLoading(false);
    }
  };

  const getDeviceInfo = () => {
    return {
      platform: navigator.platform,
      userAgent: navigator.userAgent,
      language: navigator.language,
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      timestamp: new Date().toISOString()
    };
  };

  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const ctx = canvas.getContext('2d');
    
    setIsDrawing(true);
    ctx.beginPath();
    ctx.moveTo(
      e.clientX - rect.left || e.touches[0].clientX - rect.left,
      e.clientY - rect.top || e.touches[0].clientY - rect.top
    );
  };

  const draw = (e) => {
    if (!isDrawing) return;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const ctx = canvas.getContext('2d');
    
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    
    const x = e.clientX ? e.clientX - rect.left : e.touches[0].clientX - rect.left;
    const y = e.clientY ? e.clientY - rect.top : e.touches[0].clientY - rect.top;
    
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    if (isDrawing) {
      const canvas = canvasRef.current;
      setSignatureImage(canvas.toDataURL());
    }
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setSignatureImage(null);
  };

  const handleSign = async () => {
    // Get signer info
    const signerName = document.getElementById('signer_name')?.value;
    const signerEmail = document.getElementById('signer_email')?.value;
    
    if (!signerName || !signerEmail) {
      toast.error('Please enter your name and email');
      return;
    }

    // Validate signature
    if (signatureType === 'typed') {
      if (!signature || !signature.trim()) {
        toast.error('Please enter your signature');
        return;
      }
    } else {
      if (!signatureImage) {
        toast.error('Please draw your signature');
        return;
      }
    }

    setSigning(true);
    try {
      const deviceInfo = getDeviceInfo();
      
      const response = await api.post(`/public/contracts/sign/${token}`, {
        signer_name: signerName,
        signer_email: signerEmail,
        signature_type: signatureType,
        signature_data: signatureType === 'typed' ? signature : signatureImage,
        device_info: deviceInfo
      });
      
      if (response.data.already_signed) {
        toast.info('Contract already signed');
      } else {
        toast.success('Contract signed successfully! Quote has been automatically accepted.');
      }
      setSigned(true);
    } catch (error) {
      console.error('Failed to sign contract:', error);
      toast.error(error.response?.data?.detail || 'Failed to sign contract');
    } finally {
      setSigning(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto" />
          <p className="mt-4 text-slate-600">Loading contract...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full border-red-200">
          <CardHeader className="text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <CardTitle className="text-xl text-red-700">Unable to Load Contract</CardTitle>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-slate-600 mb-4">{error}</p>
            <Button
              onClick={() => navigate('/')}
              variant="outline"
            >
              Go to Homepage
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (signed) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full border-green-200">
          <CardHeader className="text-center">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <CardTitle className="text-xl text-green-700">Contract Signed Successfully!</CardTitle>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-slate-600 mb-4">
              Thank you for signing the contract. A confirmation email has been sent to {recipientInfo?.email}.
            </p>
            <p className="text-sm text-slate-500">
              You can now close this window.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
            <FileText className="w-8 h-8 text-blue-600" />
          </div>
          <h1 className="text-3xl font-bold text-slate-800">Contract Signature Request</h1>
          <p className="text-slate-600 mt-2">Please review and sign the contract below</p>
        </div>

        {/* Recipient Info Card */}
        <Card className="mb-6 bg-blue-50 border-blue-200">
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="flex items-center space-x-3">
                <User className="w-5 h-5 text-blue-600" />
                <div>
                  <p className="text-xs text-slate-600">Recipient</p>
                  <p className="text-sm font-medium text-slate-900">{recipientInfo?.name}</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <Mail className="w-5 h-5 text-blue-600" />
                <div>
                  <p className="text-xs text-slate-600">Email</p>
                  <p className="text-sm font-medium text-slate-900">{recipientInfo?.email}</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <Calendar className="w-5 h-5 text-blue-600" />
                <div>
                  <p className="text-xs text-slate-600">Expires</p>
                  <p className="text-sm font-medium text-slate-900">
                    {new Date(recipientInfo?.expires_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Contract Content */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>{contract?.title || 'Contract'}</CardTitle>
            <div className="flex items-center space-x-4 text-sm text-slate-600 mt-2">
              {contract?.value && (
                <span>Value: {contract.currency} {contract.value}</span>
              )}
              {contract?.start_date && (
                <span>Start: {new Date(contract.start_date).toLocaleDateString()}</span>
              )}
              {contract?.end_date && (
                <span>End: {new Date(contract.end_date).toLocaleDateString()}</span>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div 
              className="prose prose-slate max-w-none bg-white p-6 rounded border border-slate-200"
              dangerouslySetInnerHTML={{ __html: contract?.content || '' }}
            />
          </CardContent>
        </Card>

        {/* Signature Card */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Sign Contract</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Signer Information */}
            <div className="grid grid-cols-2 gap-4 pb-4 border-b border-slate-200">
              <div>
                <Label htmlFor="signer_name">Your Full Name *</Label>
                <Input
                  id="signer_name"
                  placeholder="John Smith"
                  required
                />
              </div>
              <div>
                <Label htmlFor="signer_email">Your Email *</Label>
                <Input
                  id="signer_email"
                  type="email"
                  placeholder="john@example.com"
                  required
                />
              </div>
            </div>

            {/* Signature Type Toggle */}
            <div className="flex space-x-2 border-b border-slate-200 pb-3">
              <Button
                type="button"
                onClick={() => setSignatureType('typed')}
                variant={signatureType === 'typed' ? 'default' : 'outline'}
                size="sm"
                className="flex items-center"
              >
                <Type className="w-4 h-4 mr-2" />
                Type Signature
              </Button>
              <Button
                type="button"
                onClick={() => setSignatureType('drawn')}
                variant={signatureType === 'drawn' ? 'default' : 'outline'}
                size="sm"
                className="flex items-center"
              >
                <PenTool className="w-4 h-4 mr-2" />
                Draw Signature
              </Button>
            </div>

            {/* Typed Signature */}
            {signatureType === 'typed' && (
              <div>
                <Label htmlFor="signature">Your Signature *</Label>
                <Input
                  id="signature"
                  value={signature}
                  onChange={(e) => setSignature(e.target.value)}
                  placeholder="Type your full name"
                  className="mt-1 text-lg font-cursive"
                  style={{ fontFamily: 'cursive' }}
                />
                <p className="text-xs text-slate-500 mt-2">
                  By typing your name above, you agree to electronically sign this contract.
                </p>
              </div>
            )}

            {/* Drawn Signature */}
            {signatureType === 'drawn' && (
              <div>
                <Label>Draw Your Signature *</Label>
                <div className="mt-1 border-2 border-slate-300 rounded-lg bg-white relative">
                  <canvas
                    ref={canvasRef}
                    width={600}
                    height={200}
                    className="w-full cursor-crosshair touch-none"
                    onMouseDown={startDrawing}
                    onMouseMove={draw}
                    onMouseUp={stopDrawing}
                    onMouseLeave={stopDrawing}
                    onTouchStart={startDrawing}
                    onTouchMove={draw}
                    onTouchEnd={stopDrawing}
                  />
                  <Button
                    type="button"
                    onClick={clearSignature}
                    variant="ghost"
                    size="sm"
                    className="absolute top-2 right-2"
                  >
                    <RotateCcw className="w-4 h-4 mr-1" />
                    Clear
                  </Button>
                </div>
                <p className="text-xs text-slate-500 mt-2">
                  Draw your signature using your mouse or touchscreen. Click Clear to start over.
                </p>
              </div>
            )}

            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
              <p className="text-sm text-slate-700">
                <strong>By signing this contract, you agree to:</strong>
              </p>
              <ul className="text-sm text-slate-600 mt-2 space-y-1 list-disc list-inside">
                <li>The terms and conditions outlined in this contract</li>
                <li>Electronic signature having the same legal effect as handwritten signature</li>
                <li>Receipt of a signed copy via email</li>
              </ul>
            </div>

            <div className="flex items-center space-x-3 pt-4">
              <Button
                onClick={handleSign}
                disabled={signing || (signatureType === 'typed' ? !signature.trim() : !signatureImage)}
                className="bg-green-600 hover:bg-green-700 flex-1"
                size="lg"
              >
                {signing ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Signing...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-5 h-5 mr-2" />
                    Sign Contract
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center text-sm text-slate-500">
          <p>This is a secure signing page. Your information is protected.</p>
        </div>
      </div>
    </div>
  );
};

export default SignContract;
